import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL, ApiError, apiFetch } from '../../api/client';
import { SeqsimQueryType, SeqsimResult, SeqsimStatus, SeqsimView } from '../../models/Seqsim';
import './seqsim.css';
import SeqsimResults from './SeqsimResults';

const LYSOZYME_FASTA = `>P00698 Lysozyme C OS=Gallus gallus
MRSLLILVLCFLPLAALGKVFGRCELAAAMKRHGLDNYRGYSLGNWVCAAKFESNFNTQA
TNRNTDGSTDYGILQINSRWWCNDGRTPGSRNLCNIPCSALLSSDITASVNCAKKIVSDG
NGMNAWVAWRNRCKGTDVQAWIRGCRL`;

const EXAMPLES: { label: string; title: string; query: string; type: SeqsimQueryType }[] = [
   {
      label: 'P12345',
      title: 'Aspartate aminotransferase. The query itself is not in AHoJ-DB; the search returns close homologs.',
      query: 'P12345',
      type: 'uniprot',
   },
   {
      label: 'P00698',
      title: 'Lysozyme C with many AHoJ-DB entries and binding sites.',
      query: 'P00698',
      type: 'uniprot',
   },
   {
      label: 'P61626',
      title: 'A lysozyme paralog whose AlphaFold model is mixed across binding sites.',
      query: 'P61626',
      type: 'uniprot',
   },
   {
      label: 'FASTA',
      title: 'The same lysozyme sequence, searched as FASTA rather than an accession.',
      query: LYSOZYME_FASTA,
      type: 'fasta',
   },
];

function readType(raw: string | null): SeqsimQueryType {
   return raw === 'fasta' ? 'fasta' : 'uniprot';
}

function readView(raw: string | null): SeqsimView {
   return raw === 'ubs' ? 'ubs' : 'uniprot';
}

const SeqsimPage = (): React.JSX.Element => {
   const [searchParams, setSearchParams] = useSearchParams();
   const urlQuery = searchParams.get('query') ?? '';
   const urlType = readType(searchParams.get('query_type'));
   const urlMaxHits = searchParams.get('max_hits') ?? '50';
   const urlMinIdentity = searchParams.get('min_seq_id') ?? '0.30';
   const view = readView(searchParams.get('view'));

   const [draftQuery, setDraftQuery] = useState(urlQuery);
   const [draftType, setDraftType] = useState<SeqsimQueryType>(urlType);
   const [draftMaxHits, setDraftMaxHits] = useState(urlMaxHits);
   const [draftMinIdentity, setDraftMinIdentity] = useState(urlMinIdentity);

   const [status, setStatus] = useState<SeqsimStatus | null>(null);
   const [result, setResult] = useState<SeqsimResult | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [searching, setSearching] = useState(false);

   const searchKey = `${urlQuery}\n${urlType}\n${urlMaxHits}\n${urlMinIdentity}`;

   useEffect(() => {
      setDraftQuery(urlQuery);
      setDraftType(urlType);
      setDraftMaxHits(urlMaxHits);
      setDraftMinIdentity(urlMinIdentity);
   }, [urlQuery, urlType, urlMaxHits, urlMinIdentity]);

   useEffect(() => {
      const ctrl = new AbortController();
      apiFetch<SeqsimStatus>('/api/db/seqsim/status', { signal: ctrl.signal })
         .then(setStatus)
         .catch((err: unknown) => {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            setStatus({ status: 'missing', message: 'Sequence search status could not be loaded.' });
         });
      return () => ctrl.abort();
   }, []);

   useEffect(() => {
      if (status == null) return;
      if (!urlQuery.trim() || status.status === 'missing') {
         setResult(null);
         setError(null);
         setSearching(false);
         return;
      }
      const ctrl = new AbortController();
      setSearching(true);
      setError(null);
      const params = new URLSearchParams({
         query: urlQuery,
         query_type: urlType,
         max_hits: urlMaxHits,
         min_seq_id: urlMinIdentity,
      });
      apiFetch<SeqsimResult>(`/api/db/seqsim?${params.toString()}`, { signal: ctrl.signal })
         .then((data) => {
            setResult(data);
            setSearching(false);
         })
         .catch((err: unknown) => {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            setResult(null);
            setSearching(false);
            setError(err instanceof ApiError ? err.message : 'Search failed.');
         });
      return () => ctrl.abort();
   }, [searchKey, status, urlQuery, urlType, urlMaxHits, urlMinIdentity]);

   const submit = (event: React.FormEvent): void => {
      event.preventDefault();
      const next = new URLSearchParams(searchParams);
      next.set('query', draftQuery.trim());
      next.set('query_type', draftType);
      next.set('max_hits', draftMaxHits || '50');
      next.set('min_seq_id', draftMinIdentity || '0.30');
      setSearchParams(next);
   };

   const setView = (nextView: SeqsimView): void => {
      const next = new URLSearchParams(searchParams);
      next.set('view', nextView);
      setSearchParams(next, { replace: true });
   };

   const csvHref = useMemo(() => {
      const params = new URLSearchParams({
         query: urlQuery,
         query_type: urlType,
         max_hits: urlMaxHits,
         min_seq_id: urlMinIdentity,
         view,
      });
      return `${API_URL}/api/db/seqsim/export-csv?${params.toString()}`;
   }, [urlQuery, urlType, urlMaxHits, urlMinIdentity, view]);

   const downloadCsv = (): void => {
      void (async (): Promise<void> => {
         const response = await fetch(csvHref);
         if (!response.ok) {
            setError('CSV export failed.');
            return;
         }
         const blob = await response.blob();
         const objectUrl = URL.createObjectURL(blob);
         const anchor = document.createElement('a');
         anchor.href = objectUrl;
         anchor.download = `seqsim_results_${view}.csv`;
         anchor.click();
         URL.revokeObjectURL(objectUrl);
      })();
   };

   const unavailable = status?.status === 'missing';

   return (
      <article className="md-content__inner md-typeset seqsim-page">
         <h1>Sequence search</h1>
         <p className="seqsim-lead">
            Search AHoJ-DB by UniProt sequence similarity. Enter an accession or paste a protein sequence. This is
            separate from the exact PDB / UniProt / ligand search.
         </p>

         {status == null && <p className="seqsim-searching">Checking whether sequence search is available…</p>}

         {unavailable && <p className="seqsim-banner seqsim-banner--error">{status?.message}</p>}

         {status != null && !unavailable && (
            <form className="seqsim-form" onSubmit={submit}>
               <div className="seqsim-form-row">
                  <label>
                     <input
                        type="radio"
                        name="query_type"
                        checked={draftType === 'uniprot'}
                        onChange={(): void => setDraftType('uniprot')}
                     />{' '}
                     UniProt accession
                  </label>
                  <label>
                     <input
                        type="radio"
                        name="query_type"
                        checked={draftType === 'fasta'}
                        onChange={(): void => setDraftType('fasta')}
                     />{' '}
                     FASTA / sequence
                  </label>
                  <span className="seqsim-examples">
                     try:{' '}
                     {EXAMPLES.map((example) => (
                        <button
                           key={example.label}
                           type="button"
                           className="seqsim-example"
                           title={example.title}
                           onClick={(): void => {
                              setDraftType(example.type);
                              setDraftQuery(example.query);
                           }}
                        >
                           {example.label}
                        </button>
                     ))}
                  </span>
               </div>
               <textarea
                  className="seqsim-textarea"
                  rows={draftType === 'fasta' ? 6 : 3}
                  value={draftQuery}
                  onChange={(event): void => setDraftQuery(event.target.value)}
                  placeholder="P12345    or    MSEQMVAR… (accession, sequence, or FASTA)"
                  aria-label="Sequence query"
               />
               <div className="seqsim-actions">
                  <button type="submit" className="btn btn-primary" disabled={searching || !draftQuery.trim()}>
                     Search
                  </button>
                  {searching && <span className="seqsim-searching">Searching…</span>}
                  <label className="seqsim-advanced">
                     Max hits
                     <input
                        type="number"
                        min={1}
                        max={500}
                        value={draftMaxHits}
                        onChange={(event): void => setDraftMaxHits(event.target.value)}
                     />
                  </label>
                  <label className="seqsim-advanced">
                     Min identity
                     <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={draftMinIdentity}
                        onChange={(event): void => setDraftMinIdentity(event.target.value)}
                     />
                  </label>
               </div>
            </form>
         )}

         {status?.status === 'stale' && status.message && (
            <p className="seqsim-banner seqsim-banner--warn">{status.message}</p>
         )}
         {error && <p className="seqsim-banner seqsim-banner--error">{error}</p>}
         {result?.warning && <p className="seqsim-banner seqsim-banner--warn">{result.warning}</p>}
         {result?.notice && <p className="seqsim-banner">{result.notice}</p>}
         {result?.message && <p className="seqsim-banner">{result.message}</p>}

         {result && result.executed && !result.empty && (
            <div className="seqsim-results">
               <div className="seqsim-results-bar">
                  <span>
                     {result.n_hits} hit{result.n_hits === 1 ? '' : 's'}
                     {result.summary ? ` · ${result.summary.n_hits_with_entries} in AHoJ-DB` : ''}
                     {result.latency_ms != null ? ` · ${result.latency_ms} ms` : ''}
                  </span>
                  <div className="seqsim-views" role="tablist">
                     <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'uniprot'}
                        className={view === 'uniprot' ? 'is-active' : ''}
                        onClick={(): void => setView('uniprot')}
                     >
                        By UniProt
                     </button>
                     <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'ubs'}
                        className={view === 'ubs' ? 'is-active' : ''}
                        onClick={(): void => setView('ubs')}
                     >
                        By UBS
                     </button>
                  </div>
                  <button type="button" className="seqsim-csv" onClick={downloadCsv}>
                     Download CSV
                  </button>
               </div>
               <SeqsimResults hits={result.hits} view={view} />
            </div>
         )}
      </article>
   );
};

export default SeqsimPage;
